/**
 * Consumer Document Hooks
 *
 * Type-safe hooks for consumer document management using tRPC.
 * Documents are owned by the consumer via EntityAttachment(CONSUMER)
 * and can optionally be linked to cases.
 */

import { trpc } from '@/lib/trpc/client';
import { useConsumerDocument, useConsumerDocuments, useDeleteConsumerDocument } from '@/lib/trpc/hooks';

/**
 * Case link info — describes how a document is linked to a case.
 */
export interface CaseLinkInfo {
  caseId: string;
  caseNumber: string;
  verificationStatus: string;
  rejectionReason: string | null;
  attachmentId: string;
}

/**
 * A consumer-owned document with optional case links.
 * Supports both the new API response shape (with caseLinks, processingStatus)
 * and the legacy shape (with status, caseId) for backwards compatibility.
 */
export interface ConsumerDocument {
  id: string;
  documentId: string;
  name: string;
  size: number;
  mimeType: string;
  documentType: string | null;
  category: string | null;
  processingStatus: string;
  uploadedAt: string;
  caseLinks: CaseLinkInfo[];
}

export interface DocumentsResponse {
  consumerId: string;
  documents: ConsumerDocument[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Normalizes a backend document response (old or new shape) into ConsumerDocument.
 */
function normalizeDocument(raw: Record<string, unknown>): ConsumerDocument {
  return {
    id: (raw.id as string) ?? '',
    documentId: (raw.documentId as string) ?? (raw.id as string) ?? '',
    name: (raw.name as string) ?? 'Unnamed',
    size: (raw.size as number) ?? (raw.fileSize as number) ?? 0,
    mimeType: (raw.mimeType as string) ?? (raw.fileType as string) ?? '',
    documentType: (raw.documentType as string) ?? null,
    category: (raw.category as string) ?? null,
    // New field: processingStatus. Fallback to legacy "status" field.
    processingStatus: (raw.processingStatus as string) ?? (raw.status as string) ?? 'pending',
    // New field: uploadedAt. Fallback to legacy "createdAt" field.
    uploadedAt: (raw.uploadedAt as string) ?? (raw.createdAt as string) ?? new Date().toISOString(),
    // New field: caseLinks array. Construct from legacy caseId if needed.
    caseLinks: Array.isArray(raw.caseLinks)
      ? (raw.caseLinks as CaseLinkInfo[])
      : raw.caseId
        ? [
            {
              caseId: raw.caseId as string,
              caseNumber: '',
              verificationStatus: (raw.rejectionReason as string)
                ? 'REJECTED'
                : (raw.status as string)?.toUpperCase() === 'VERIFIED'
                  ? 'VERIFIED'
                  : 'PENDING',
              rejectionReason: (raw.rejectionReason as string) ?? null,
              attachmentId: (raw.attachmentId as string) ?? '',
            },
          ]
        : [],
  };
}

// Export tRPC hooks with consumer-friendly names
export function useDocuments(page = 1, limit = 20) {
  const query = useConsumerDocuments({ page, limit });
  return {
    ...query,
    data: query.data
      ? {
          consumerId: (query.data as Record<string, unknown>).consumerId as string | undefined,
          documents: ((query.data.data ?? []) as Record<string, unknown>[]).map(normalizeDocument),
          total: query.data.total,
          page: query.data.page,
          limit: query.data.limit,
        }
      : undefined,
  };
}

export function useDocument(id: string) {
  return useConsumerDocument(id);
}

export function useDeleteDocument() {
  return useDeleteConsumerDocument();
}

/**
 * Helper: Convert File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const parts = result.split(',');
      const base64 = parts[1];
      if (!base64) {
        reject(new Error('Invalid file data URL'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Upload document using tRPC with base64 encoding.
 * Documents are owned by the consumer and caseId is optional.
 * When caseId is provided, the document is also linked to that case.
 */
export function useUploadDocument() {
  const utils = trpc.useUtils();
  const uploadMutation = trpc.consumerDocuments.upload.useMutation({
    onSuccess: () => {
      // Invalidate document queries to refresh the list
      utils.consumerDocuments.list.invalidate();
      // Also invalidate case queries since documents may be linked to cases
      utils.consumerCases.byId.invalidate();
      utils.consumerCases.list.invalidate();
    },
  });

  return {
    ...uploadMutation,
    mutateAsync: async ({
      file,
      caseId,
      documentType,
      category,
      description,
      replacesAttachmentId,
    }: {
      file: File;
      caseId?: string;
      documentType?: string;
      category?: string;
      description?: string;
      /** When re-uploading a rejected document, pass the old EntityAttachment ID */
      replacesAttachmentId?: string;
    }) => {
      // Validate file size (50MB max)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size exceeds maximum allowed size of 50MB');
      }

      // Convert file to base64
      const fileData = await fileToBase64(file);

      // Call tRPC mutation — caseId is optional (consumer-owned documents can exist without a case)
      return uploadMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileData,
        description,
        isPublic: false,
        replacesAttachmentId,
        documentType,
        category,
        caseId,
      } as Parameters<typeof uploadMutation.mutateAsync>[0]);
    },
  };
}

/**
 * Link an existing consumer document to a case via the Data Room.
 */
export function useLinkToCase() {
  const utils = trpc.useUtils();
  return trpc.dataRoom.linkDocument.useMutation({
    onSuccess: () => {
      utils.consumerDocuments.list.invalidate();
    },
  });
}

/**
 * Download document content for preview. Returns base64 content + mimeType.
 * Only fetches when documentId is provided (non-null).
 */
export function useDocumentDownload(documentId: string | null) {
  return trpc.consumerDocuments.download.useQuery({ documentId: documentId! }, { enabled: !!documentId });
}

export default useDocuments;
